import {
  readFaceCaptureDataUrl,
  readFaceProfileSnapshot,
} from '../../../../enterprise/face-identity/utils/faceIdentityApi';
import { resolveFacePortraitUrl } from '../../../../enterprise/face-identity/utils/facePortrait';
import { readFaceSalonProfile } from '../../../../enterprise/salon/utils/bootstrapFaceSalonSession';
import { readVisitHandoff } from '../../../../enterprise/salon/utils/visitHandoff';
import { resolveInsightAssetUrl } from './insightApi';

function titleCase(s) {
  return String(s || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeHairPhotoUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:') || trimmed.startsWith('http')) {
    return trimmed;
  }
  return resolveInsightAssetUrl(trimmed) || trimmed;
}

/** Full hair overview from step 1 (wide capture), for report / reference when face ID photo is absent. */
export function resolveHairWideOverviewUrl(dashboard) {
  if (!dashboard || typeof dashboard !== 'object') return '';
  const storyUrl = dashboard?.visualStory?.where?.imageUrl;
  if (storyUrl) return normalizeHairPhotoUrl(storyUrl);
  const wideOverlayUrl = dashboard?.wideOverlay?.originalUrl;
  if (wideOverlayUrl) return normalizeHairPhotoUrl(wideOverlayUrl);
  const caps = Array.isArray(dashboard.captures) ? dashboard.captures : [];
  const wide =
    caps.find((c) => c?.source === 'wide_camera' && c?.view === 'overview') ||
    caps.find((c) => c?.source === 'wide_camera' && c?.view === 'front') ||
    caps.find((c) => c?.source === 'wide_camera');
  const url = wide?.imageUrl;
  return url ? normalizeHairPhotoUrl(url) : '';
}

export function readStoredWidePreviewUrl(sessionId) {
  if (!sessionId || typeof sessionStorage === 'undefined') return '';
  try {
    const cached = sessionStorage.getItem(hairWidePreviewStorageKey(sessionId));
    return cached ? normalizeHairPhotoUrl(cached) : '';
  } catch {
    return '';
  }
}

export function resolveWideCaptureFromSession(session) {
  if (!session || typeof session !== 'object') return '';
  const caps = Array.isArray(session.captures) ? session.captures : [];
  const wide =
    caps.find((c) => c?.source === 'wide_camera' && c?.view === 'overview') ||
    caps.find((c) => c?.source === 'wide_camera' && c?.view === 'front') ||
    caps.find((c) => c?.source === 'wide_camera');
  const url = wide?.imageUrl;
  return url ? resolveInsightAssetUrl(url) || '' : '';
}

const HAIR_WIDE_PREVIEW_STORAGE_PREFIX = 'hi-insight-wide-preview:';

export function hairWidePreviewStorageKey(sessionId) {
  return `${HAIR_WIDE_PREVIEW_STORAGE_PREFIX}${sessionId || ''}`;
}

/** Customer profile from face identity / salon visit for Hair Insight report. */
export function resolveHairReportCustomer(dashboard) {
  const profile = readFaceSalonProfile();
  const handoff = readVisitHandoff() || {};
  const snapshot = readFaceProfileSnapshot() || {};
  const sessionId = dashboard?.sessionId || '';
  const hairOverviewUrl =
    resolveHairWideOverviewUrl(dashboard) || readStoredWidePreviewUrl(sessionId);
  const captureUrl = normalizeHairPhotoUrl(
    resolveFacePortraitUrl(
      readFaceCaptureDataUrl(),
      handoff,
      snapshot,
      snapshot.user,
      snapshot.profile,
      dashboard?.customer,
      dashboard?.guest,
    ) ||
      hairOverviewUrl ||
      '',
  );

  const name =
    profile.name ||
    handoff.customerName ||
    (dashboard?.customerId && dashboard.customerId !== 'GUEST' ? dashboard.customerId : '') ||
    'Guest';

  const mobile = profile.mobile || handoff.mobile || '';
  const email = profile.email || handoff.email || '';
  const gender = profile.gender
    ? titleCase(profile.gender)
    : handoff.gender
      ? titleCase(handoff.gender)
      : snapshot.profile?.gender
        ? titleCase(snapshot.profile.gender)
        : snapshot.user?.gender
          ? titleCase(snapshot.user.gender)
          : '';
  const age =
    profile.age != null && String(profile.age).trim() !== ''
      ? String(profile.age)
      : handoff.age != null && String(handoff.age).trim() !== ''
        ? String(handoff.age)
        : '';
  const customerCode = handoff.customerCode || handoff.checkinCode || '';

  return {
    name,
    mobile,
    email,
    gender,
    age,
    customerCode,
    photoUrl: captureUrl,
    initial: String(name).trim().charAt(0).toUpperCase() || 'G',
    hasIdentity: Boolean(
      captureUrl || (name && name !== 'Guest') || mobile || customerCode,
    ),
  };
}
